import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { isLocale, type Locale } from '@/client/i18n';
import { useConsent } from './useConsent';
import { Cookie } from 'lucide-react';

// Cookie consent banner. ÚOOÚ enforcement 2024-2025: "Súhlasím" and
// "Odmietnuť všetko" must have equal visual prominence. Both buttons here use
// the same size, padding, and chip styling — the only difference is
// foreground/background contrast (gradient purple for accept, glass for
// reject), which keeps them visually distinct without privileging one over
// the other.

export function CookieBanner() {
  const { t } = useTranslation();
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const { shouldShowBanner, acceptAll, rejectAll } = useConsent();

  if (!shouldShowBanner) return null;

  const cookiesPath = locale === 'cs' ? '/cs/cookies' : '/sk/cookies';

  return (
    <div
      role="dialog"
      aria-modal="false"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-body"
      className="fixed bottom-4 left-4 right-4 z-50 sm:left-6 sm:right-6 md:left-1/2 md:right-auto md:-translate-x-1/2 md:bottom-6 md:max-w-2xl"
    >
      <div className="dr-card-strong p-5 sm:p-6 shadow-[0_24px_64px_rgba(81,45,109,0.22)] backdrop-blur-2xl bg-white/85">
        <div className="flex items-start gap-4">
          <div
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full"
            style={{
              background:
                'conic-gradient(from 220deg, rgba(193,167,226,0.55), rgba(117,59,189,0.20), rgba(193,167,226,0.55))',
            }}
            aria-hidden
          >
            <Cookie size={18} className="text-primary-dark" />
          </div>
          <div className="flex-1 min-w-0">
            <h2
              id="cookie-banner-title"
              className="font-display text-h5 text-primary-dark"
            >
              {t('cookies.banner_title')}
            </h2>
            <p
              id="cookie-banner-body"
              className="mt-1.5 font-sans text-body-small text-text-secondary leading-relaxed"
            >
              {t('cookies.banner_body')}{' '}
              <Link
                to={cookiesPath}
                className="font-medium text-primary underline-offset-2 hover:underline"
              >
                {t('cookies.banner_link')}
              </Link>
              .
            </p>
            <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:gap-3">
              <button
                type="button"
                onClick={rejectAll}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-pill border border-glass-border-strong bg-white/70 px-5 font-sans text-body-small font-medium text-text-primary backdrop-blur-md transition-all hover:-translate-y-px hover:bg-white hover:shadow-[0_4px_16px_rgba(81,45,109,0.10)]"
              >
                {t('cookies.banner_reject')}
              </button>
              <button
                type="button"
                onClick={acceptAll}
                className="inline-flex h-10 flex-1 items-center justify-center rounded-pill bg-gradient-to-br from-primary to-primary-dark px-5 font-sans text-body-small font-medium text-white shadow-[0_4px_16px_rgba(117,59,189,0.28)] transition-all hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(117,59,189,0.38)]"
              >
                {t('cookies.banner_accept')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
